import type {
  BigQueryDataset,
  BigQueryTable,
  IntegrationResourceContext,
  ProviderAccount,
  ProviderAccountParams,
} from '../models/integrationResources.types'

import { appHttp } from '@/shared/http'

type ResourcesHttp = Pick<typeof appHttp, 'get'>
const OWNED = { suppressGlobalErrorHandler: true, meta: { suppressGlobalErrorHandler: true } }

function listFrom<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[]
  const data = body && typeof body === 'object' ? (body as Record<string, unknown>).data : undefined
  return Array.isArray(data) ? (data as T[]) : []
}

export class IntegrationResourcesApi {
  constructor(private readonly http: ResourcesHttp = appHttp) {}

  async fetchAccounts(
    workspaceId: number | string,
    endpoint: string,
    params?: ProviderAccountParams,
    signal?: AbortSignal,
  ): Promise<ProviderAccount[]> {
    const response = await this.http.get<unknown>(`/integrations/${workspaceId}/${endpoint}`, {
      ...OWNED,
      ...(params ? { params } : {}),
      ...(signal ? { signal } : {}),
    })
    return listFrom<ProviderAccount>(response.data)
  }

  async listDatasets(
    context: IntegrationResourceContext,
    projectId: string,
    signal?: AbortSignal,
  ): Promise<BigQueryDataset[]> {
    const response = await this.http.get<unknown>('/bq-datasets/', {
      ...OWNED,
      ...(signal ? { signal } : {}),
      params: {
        account_id: context.accountId,
        workspace_id: context.workspaceId,
        project_id: projectId,
      },
    })
    return listFrom<Record<string, unknown>>(response.data).map((dataset) => {
      const reference = (dataset.datasetReference ?? {}) as Record<string, unknown>
      return {
        ...dataset,
        datasetId: String(reference.datasetId ?? dataset.datasetId ?? ''),
        projectId: String(reference.projectId ?? dataset.projectId ?? projectId),
      }
    })
  }

  async listTables(
    context: IntegrationResourceContext,
    projectId: string,
    datasetId: string,
    signal?: AbortSignal,
  ): Promise<BigQueryTable[]> {
    const response = await this.http.get<unknown>('/bq-tables/', {
      ...OWNED,
      ...(signal ? { signal } : {}),
      params: {
        account_id: context.accountId,
        workspace_id: context.workspaceId,
        project_id: projectId,
        dataset_id: datasetId,
      },
    })
    return listFrom<Record<string, unknown>>(response.data).map((table) => {
      const reference = (table.tableReference ?? {}) as Record<string, unknown>
      const tableId = String(reference.tableId ?? table.tableId ?? table.id ?? '')
      return { ...table, id: String(table.id ?? tableId), tableId }
    })
  }

  async listFields(
    context: IntegrationResourceContext,
    projectId: string,
    datasetId: string,
    tableId: string,
    signal?: AbortSignal,
  ): Promise<string[]> {
    const response = await this.http.get<unknown>('/bq-fields/', {
      ...OWNED,
      ...(signal ? { signal } : {}),
      params: {
        account_id: context.accountId,
        workspace_id: context.workspaceId,
        project_id: projectId,
        dataset_id: datasetId,
        table_id: tableId,
      },
    })
    return listFrom<unknown>(response.data).map(String).filter(Boolean)
  }
}

export const integrationResourcesApi = new IntegrationResourcesApi()
